import React, { useMemo } from 'react';
import './CapacityTimeline.css';

/**
 * F7 — CapacityTimeline.
 *
 * Renders a horizontal grid of slot cells for one service for "today".
 * Booked slots overlay in `accent.primary`; the lunch break is a diagonal-
 * stripe band; the current moment is a 2px tick.
 *
 * Props:
 *   openHour, closeHour   — integer hours, 0..24 (closeHour > openHour)
 *   slotMinutes           — typically 15/30/60
 *   bookedSlots           — Array<{ start: Date, end: Date }>
 *   lunchBreak            — { start: Date, end: Date } | null
 *   currentNow            — Date | null (today's "now" tick)
 *   slotsTotal, booked, open — counts for the header line
 *   emptyMessage          — when there's no summary, shows the cell grid greyed-out
 */
const AXIS_HOURS_DEFAULT = [9, 12, 15, 18, 21];

const minutesFromOpen = (date, baseHour) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.getHours() * 60 + date.getMinutes() - baseHour * 60;
};

const formatHourLabel = (hour) => {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  if (hour < 12) return `${hour}a`;
  return `${hour - 12}p`;
};

const CapacityTimeline = ({
  openHour = 9,
  closeHour = 21,
  slotMinutes = 30,
  bookedSlots = [],
  lunchBreak = null,
  currentNow = null,
  slotsTotal,
  booked,
  open,
  emptyMessage = 'No data yet',
}) => {
  const cellCount = Math.max(0, Math.round(((closeHour - openHour) * 60) / slotMinutes));
  const totalMinutes = Math.max(1, (closeHour - openHour) * 60);

  const bookedRanges = useMemo(() => {
    if (!Array.isArray(bookedSlots)) return [];
    return bookedSlots
      .map((slot) => {
        const startMin = minutesFromOpen(slot.start, openHour);
        const endMin = minutesFromOpen(slot.end, openHour);
        if (startMin === null || endMin === null) return null;
        const clampedStart = Math.max(0, startMin);
        const clampedEnd = Math.min(totalMinutes, endMin);
        if (clampedEnd <= clampedStart) return null;
        return { startPct: (clampedStart / totalMinutes) * 100, endPct: (clampedEnd / totalMinutes) * 100 };
      })
      .filter(Boolean);
  }, [bookedSlots, openHour, totalMinutes]);

  const lunchRange = useMemo(() => {
    if (!lunchBreak?.start || !lunchBreak?.end) return null;
    const startMin = minutesFromOpen(lunchBreak.start, openHour);
    const endMin = minutesFromOpen(lunchBreak.end, openHour);
    if (startMin === null || endMin === null) return null;
    const clampedStart = Math.max(0, startMin);
    const clampedEnd = Math.min(totalMinutes, endMin);
    if (clampedEnd <= clampedStart) return null;
    return { startPct: (clampedStart / totalMinutes) * 100, endPct: (clampedEnd / totalMinutes) * 100 };
  }, [lunchBreak, openHour, totalMinutes]);

  const nowPct = useMemo(() => {
    const min = minutesFromOpen(currentNow, openHour);
    if (min === null) return null;
    if (min < 0 || min > totalMinutes) return null;
    return (min / totalMinutes) * 100;
  }, [currentNow, openHour, totalMinutes]);

  const axisHours = AXIS_HOURS_DEFAULT.filter((h) => h >= openHour && h <= closeHour);

  const hasSummary = slotsTotal != null;
  const showEmpty = !hasSummary && bookedRanges.length === 0;

  return (
    <div className={`appointments-v3-timeline ${showEmpty ? 'is-empty' : ''}`}>
      {hasSummary && (
        <div className="appointments-v3-timeline-summary">
          <span className="appointments-v3-timeline-summary-num">{slotsTotal} slots</span>
          {booked != null && <span className="appointments-v3-timeline-summary-sep">·</span>}
          {booked != null && (
            <span className="appointments-v3-timeline-summary-booked">{booked} booked</span>
          )}
          {open != null && <span className="appointments-v3-timeline-summary-sep">·</span>}
          {open != null && (
            <span className="appointments-v3-timeline-summary-open">{open} open</span>
          )}
        </div>
      )}
      <div className="appointments-v3-timeline-track" role="img" aria-label="Capacity timeline">
        <div className="appointments-v3-timeline-cells">
          {Array.from({ length: cellCount }).map((_, idx) => (
            <div key={idx} className="appointments-v3-timeline-cell" />
          ))}
        </div>
        {lunchRange && (
          <div
            className="appointments-v3-timeline-lunch"
            style={{
              left: `${lunchRange.startPct}%`,
              width: `${lunchRange.endPct - lunchRange.startPct}%`,
            }}
            aria-hidden="true"
          />
        )}
        {bookedRanges.map((r, idx) => (
          <div
            key={`booked-${idx}`}
            className="appointments-v3-timeline-booked"
            style={{ left: `${r.startPct}%`, width: `${r.endPct - r.startPct}%` }}
            aria-hidden="true"
          />
        ))}
        {nowPct !== null && (
          <div
            className="appointments-v3-timeline-now"
            style={{ left: `${nowPct}%` }}
            aria-hidden="true"
          />
        )}
        {showEmpty && (
          <div className="appointments-v3-timeline-empty">{emptyMessage}</div>
        )}
      </div>
      <div className="appointments-v3-timeline-axis" aria-hidden="true">
        {axisHours.map((h) => {
          const pct = ((h - openHour) * 60 / totalMinutes) * 100;
          return (
            <span
              key={h}
              className="appointments-v3-timeline-axis-tick"
              style={{ left: `${pct}%` }}
            >
              {formatHourLabel(h)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(CapacityTimeline, (prev, next) =>
  prev.openHour === next.openHour
  && prev.closeHour === next.closeHour
  && prev.slotMinutes === next.slotMinutes
  && prev.slotsTotal === next.slotsTotal
  && prev.booked === next.booked
  && prev.open === next.open
  && prev.bookedSlots === next.bookedSlots
  && prev.lunchBreak === next.lunchBreak
  && prev.currentNow === next.currentNow
);
